import {
  UpdateSessionTemplateSchema,
  type TemplateModule,
  type SessionTemplateDetail,
  type UpdateSessionTemplate,
} from "shared"
import { moveItem } from "../utils/moveItem"

/** What the administrator changes on a saved template. */
export interface TemplateContent {
  name: string
  description: string
  /** The programmes the template is for, as the picker holds them. */
  programIds: string[]
  /** The service types it is for, on the same terms. */
  serviceTypeIds: string[]
  modules: TemplateModule[]
}

/**
 * The administrator's working copy of a template: its content as edited, and
 * as the server holds it, at the revision it is stored at, which a save names
 * as the one it was edited from.
 */
export interface TemplateState {
  content: TemplateContent
  stored: TemplateContent
  revision: number
}

/** Everything that can happen to a template as edited, each applied whole. */
export type TemplateEvent =
  | { type: "renamed"; name: string }
  | { type: "described"; description: string }
  | { type: "added"; module: TemplateModule; at: number }
  | { type: "removed"; id: string }
  | { type: "moved"; index: number; by: number }
  | { type: "programsChanged"; ids: string[] }
  | { type: "serviceTypesChanged"; ids: string[] }
  | { type: "saved"; sent: TemplateContent; template: SessionTemplateDetail }
  | { type: "reloaded"; template: SessionTemplateDetail }

const contentOf = (template: SessionTemplateDetail): TemplateContent => ({
  name: template.name,
  description: template.description,
  programIds: template.programs.map((program) => program.id),
  serviceTypeIds: template.serviceTypes.map((type) => type.id),
  modules: template.modules,
})

export const seedTemplateState = (
  template: SessionTemplateDetail,
): TemplateState => ({
  content: contentOf(template),
  stored: contentOf(template),
  revision: template.revision,
})

export function templateReducer(
  state: TemplateState,
  event: TemplateEvent,
): TemplateState {
  switch (event.type) {
    case "renamed":
      return { ...state, content: { ...state.content, name: event.name } }
    case "described":
      return {
        ...state,
        content: { ...state.content, description: event.description },
      }
    case "added":
      return {
        ...state,
        content: {
          ...state.content,
          modules: [
            ...state.content.modules.slice(0, event.at),
            event.module,
            ...state.content.modules.slice(event.at),
          ],
        },
      }
    case "removed":
      return {
        ...state,
        content: {
          ...state.content,
          modules: state.content.modules.filter(
            (module) => module.id !== event.id,
          ),
        },
      }
    case "programsChanged":
      return {
        ...state,
        content: { ...state.content, programIds: event.ids },
      }
    case "serviceTypesChanged":
      return {
        ...state,
        content: { ...state.content, serviceTypeIds: event.ids },
      }
    case "moved":
      return {
        ...state,
        content: {
          ...state.content,
          modules: moveItem(state.content.modules, event.index, event.by),
        },
      }
    case "saved": {
      // The server's copy is what is stored either way. It replaces what is on
      // screen only if nothing changed while the save was in flight; otherwise
      // that later change is kept, and still counts as unsaved.
      const stored = contentOf(event.template)
      const { revision } = event.template
      return state.content === event.sent
        ? { content: stored, stored, revision }
        : { ...state, stored, revision }
    }
    // Someone else saved: what was edited here gives way to what is stored.
    case "reloaded":
      return seedTemplateState(event.template)
  }
}

// The content as the update the server would receive, checked by the schema
// that owns its rules: names trimmed, lengths held. Memoised per content, as a
// render asks more than once.
const ContentSchema = UpdateSessionTemplateSchema.omit({ revision: true })
const checks = new WeakMap<
  TemplateContent,
  ReturnType<typeof ContentSchema.safeParse>
>()

const checked = (content: TemplateContent) => {
  const held = checks.get(content)
  if (held) return held
  const outcome = ContentSchema.safeParse({
    name: content.name,
    description: content.description,
    programIds: content.programIds,
    serviceTypeIds: content.serviceTypeIds,
    moduleIds: content.modules.map((module) => module.id),
  })
  checks.set(content, outcome)
  return outcome
}

/** Whether the server would accept the template as edited. */
export const canSave = ({ content }: TemplateState) => checked(content).success

/**
 * The update to send for this content, edited from `revision`. Asked only of
 * content the schema accepts — Save waits on canSave — so a refusal here is a
 * fault, and thrown.
 */
export function toUpdate(
  content: TemplateContent,
  revision: number,
): UpdateSessionTemplate {
  const outcome = checked(content)
  if (!outcome.success) throw outcome.error
  return { ...outcome.data, revision }
}

// The content as the server would store it, when the schema accepts it; as it
// is when it does not, which differs from anything stored.
const asStored = (content: TemplateContent): TemplateContent => {
  const outcome = checked(content)
  return outcome.success
    ? {
        ...content,
        name: outcome.data.name,
        description: outcome.data.description,
      }
    : content
}

/**
 * How each part of a template, as stored, is told apart from the server's
 * copy. Typed over every key of TemplateContent, so a part added to it does
 * not compile until it says how it is compared — a part left out would never
 * count as a change, and Cancel would drop it without asking.
 */
const sameAsStored: {
  [K in keyof TemplateContent]: (
    edited: TemplateContent[K],
    stored: TemplateContent[K],
  ) => boolean
} = {
  name: (edited, stored) => edited === stored,
  description: (edited, stored) => edited === stored,
  // Sets, not sequences: the server orders programmes and service types by
  // name while the picker appends, and the same ones are the same template.
  programIds: sameSet,
  serviceTypeIds: sameSet,
  // In order, by id: the order is the template's, and a module's details are
  // not edited here.
  modules: (edited, stored) =>
    edited.length === stored.length &&
    edited.every((module, i) => module.id === stored[i].id),
}

const isUnchanged = <K extends keyof TemplateContent>(
  key: K,
  edited: TemplateContent,
  stored: TemplateContent,
) => sameAsStored[key](edited[key], stored[key])

/** Whether the template as edited differs from what the server holds. */
export const hasUnsavedChanges = ({ content, stored }: TemplateState) => {
  const edited = asStored(content)
  return (Object.keys(sameAsStored) as (keyof TemplateContent)[]).some(
    (key) => !isUnchanged(key, edited, stored),
  )
}

function sameSet(a: string[], b: string[]) {
  const inA = new Set(a)
  const inB = new Set(b)
  return inA.size === inB.size && [...inA].every((id) => inB.has(id))
}
